import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Drawer, useTheme, useMediaQuery, Button } from '@mui/material';
import { Menu, Settings } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import useThemeStyles from '../../../../hooks/useThemeStyles';
import CenterContent from '../CenterContent/CenterContent';
import { getSidebarConfig } from '../sidebarConfig';

// Resize Handle Component
const ResizeHandle = ({
  type,
  position,
  onMouseDown,
  isResizing,
  resizeType = null,
  primaryColor,
}) => (
  <Box
    onMouseDown={(e) => onMouseDown(e, type)}
    sx={{
      position: 'absolute',
      [position]: 0,
      top: 0,
      bottom: 0,
      width: '4px',
      cursor: 'col-resize',
      backgroundColor: 'transparent',
      zIndex: 1001,
      '&:hover': {
        backgroundColor: primaryColor,
        opacity: 0.3,
      },
      ...(isResizing &&
        resizeType === type && {
          backgroundColor: primaryColor,
          opacity: 0.5,
        }),
    }}
  />
);

/* eslint-disable react/require-default-props */
ResizeHandle.propTypes = {
  type: PropTypes.string.isRequired,
  position: PropTypes.string.isRequired,
  onMouseDown: PropTypes.func.isRequired,
  isResizing: PropTypes.bool.isRequired,
  resizeType: PropTypes.string,
  primaryColor: PropTypes.string.isRequired,
};
/* eslint-enable react/require-default-props */

const ProjectLayout = ({
  // Layout state
  leftSidebarOpen,
  rightSidebarOpen,
  toggleLeftSidebar,
  toggleRightSidebar,
  leftSidebarWidth,
  rightSidebarWidth,
  handleLeftSidebarResize,
  handleRightSidebarResize,

  // Project data
  activeProject = null,
  chapters = [],
  notes = [],
  characters = [],
  collaborators = [],

  // Handlers
  handleBackToDashboard,
  handleEditProject,
  handleAddChapter,
  handleAddNote,
  handleAddCharacter = null,
  onCharacterClick = null,
  handleChapterClick,
  handleAddScene,
  handleSceneClick = null,
  setRefreshScenesCallback,
  onSceneAdded,
  formatDate,
  suppressSceneLoading = false,
  handleExportProject,
  handleImportDocument,
  handleShareProject,
  activeCenterSection = 'manuscript',
  notesView = 'dashboard',
  corkboardId = null,
  timelineId = null,
  gameTool = 'erd',
  onGameToolChange = null,
  onCenterSectionChange,
}) => {
  const theme = useTheme();
  const { colors } = useThemeStyles();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [isResizing, setIsResizing] = useState(false);
  const [resizeType, setResizeType] = useState(null);

  // Get sidebar configuration based on current route
  const sidebarConfig = getSidebarConfig(location.pathname);

  // Get sidebar components and props
  const LeftSidebarComponent = sidebarConfig?.leftSidebar || null;
  const RightSidebarComponent = sidebarConfig?.rightSidebar || null;

  // Prepare props for sidebars
  const allProps = {
    activeProject,
    chapters,
    notes,
    characters,
    collaborators,
    handleBackToDashboard,
    handleEditProject,
    handleAddChapter,
    handleAddNote,
    handleAddCharacter,
    onCharacterClick,
    handleChapterClick,
    handleAddScene,
    handleSceneClick,
    setRefreshScenesCallback,
    onSceneAdded,
    formatDate,
    suppressSceneLoading,
    handleExportProject,
    handleImportDocument,
    handleShareProject,
    activeCenterSection,
    notesView,
    corkboardId,
    timelineId,
    gameTool,
    onGameToolChange,
    onCenterSectionChange,
    toggleLeftSidebar,
    toggleRightSidebar,
  };

  const leftSidebarProps = sidebarConfig?.leftSidebarProps
    ? sidebarConfig.leftSidebarProps(allProps)
    : {};
  const rightSidebarProps = sidebarConfig?.rightSidebarProps
    ? sidebarConfig.rightSidebarProps(allProps)
    : {};

  const handleMouseDown = useCallback((e, type) => {
    e.preventDefault();
    setIsResizing(true);
    setResizeType(type);
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (!isResizing || !resizeType) return;

      const containerRect = e.currentTarget.getBoundingClientRect();
      const newWidth =
        resizeType === 'left' ? e.clientX - containerRect.left : containerRect.right - e.clientX;

      if (resizeType === 'left') {
        handleLeftSidebarResize(newWidth);
      } else {
        handleRightSidebarResize(newWidth);
      }
    },
    [isResizing, resizeType, handleLeftSidebarResize, handleRightSidebarResize]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    setResizeType(null);
  }, []);

  return (
    <Box
      sx={{ display: 'flex', flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden' }}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Floating Toggle Buttons */}
      {LeftSidebarComponent && !leftSidebarOpen && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 10,
            zIndex: 1000,
          }}
        >
          <Button
            variant="contained"
            size="small"
            startIcon={<Menu sx={{ color: colors.icon.primary }} />}
            onClick={toggleLeftSidebar}
            sx={{
              backgroundColor: colors.paper.background,
              color: colors.icon.primary,
              textTransform: 'none',
              borderRadius: '0 8px 8px 0',
              boxShadow: '2px 2px 8px rgba(0, 0, 0, 0.2)',
              paddingLeft: '20px',
              paddingRight: '16px',
              border: '1px solid',
              borderColor: colors.border.subtle,
              borderLeft: 'none',
              '&:hover': {
                backgroundColor: colors.background.surface,
                boxShadow: '3px 3px 12px rgba(0, 0, 0, 0.3)',
              },
            }}
          >
            Expand
          </Button>
        </Box>
      )}

      {RightSidebarComponent && !rightSidebarOpen && (
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: 10,
            zIndex: 1000,
          }}
        >
          <Button
            variant="contained"
            size="small"
            startIcon={<Settings sx={{ color: colors.icon.primary }} />}
            onClick={toggleRightSidebar}
            sx={{
              backgroundColor: colors.paper.background,
              color: colors.icon.primary,
              textTransform: 'none',
              borderRadius: '8px 0 0 8px',
              boxShadow: '-2px 2px 8px rgba(0, 0, 0, 0.2)',
              paddingLeft: '16px',
              paddingRight: '20px',
              border: '1px solid',
              borderColor: colors.border.subtle,
              borderRight: 'none',
              '&:hover': {
                backgroundColor: colors.background.surface,
                boxShadow: '-3px 3px 12px rgba(0, 0, 0, 0.3)',
              },
            }}
          >
            Expand
          </Button>
        </Box>
      )}

      {/* Left Sidebar */}
      {LeftSidebarComponent && (
        <Drawer
          variant={isMobile ? 'temporary' : 'persistent'}
          anchor="left"
          open={leftSidebarOpen}
          onClose={toggleLeftSidebar}
          sx={{
            width: leftSidebarOpen ? leftSidebarWidth : 0,
            height: '100%',
            flexShrink: 0,
            transition: isResizing
              ? 'none'
              : theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
            '& .MuiDrawer-paper': {
              position: 'relative',
              whiteSpace: 'nowrap',
              width: leftSidebarWidth,
              height: '100%',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              transition: isResizing
                ? 'none'
                : theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                  }),
              borderRight: leftSidebarOpen ? 'none' : '3px solid',
              borderRightColor: colors.primary.main,
              backgroundColor: leftSidebarOpen
                ? colors.paper.background
                : colors.background.secondary,
            },
          }}
        >
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          {LeftSidebarComponent && <LeftSidebarComponent {...leftSidebarProps} />}
          {/* Resize Handle for Left Sidebar */}
          {leftSidebarOpen && !isMobile && (
            <ResizeHandle
              type="left"
              position="right"
              onMouseDown={handleMouseDown}
              isResizing={isResizing}
              resizeType={resizeType}
              primaryColor={colors.primary.main}
            />
          )}
        </Drawer>
      )}

      {/* Main Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0, // Prevents flex item from overflowing
          minHeight: 0, // Allows proper flex shrinking
          overflow: 'hidden',
          paddingLeft: 0,
          paddingRight: 0,
          transition: theme.transitions.create(['padding-left', 'padding-right'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <CenterContent
          activeProject={activeProject}
          chapters={chapters}
          notes={notes}
          characters={characters}
          activeSection={activeCenterSection}
          notesView={notesView}
          corkboardId={corkboardId}
          timelineId={timelineId}
          gameTool={gameTool}
          onGameToolChange={onGameToolChange}
          rightSidebarOpen={rightSidebarOpen}
          onChapterClick={handleChapterClick}
          onSceneAdded={onSceneAdded}
          onAddScene={handleAddScene}
          suppressSceneLoading={suppressSceneLoading}
        />
      </Box>

      {/* Right Sidebar */}
      {RightSidebarComponent && (
        <Drawer
          variant={isMobile ? 'temporary' : 'persistent'}
          anchor="right"
          open={rightSidebarOpen}
          onClose={toggleRightSidebar}
          sx={{
            width: rightSidebarOpen ? rightSidebarWidth : 0,
            minWidth: rightSidebarOpen ? 336 : 0, // 56 (mode switcher) + 280 (min content)
            height: '100%',
            flexShrink: 1, // Allow shrinking but respect minWidth
            transition: isResizing
              ? 'none'
              : theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
            '& .MuiDrawer-paper': {
              position: 'relative',
              whiteSpace: 'normal', // Allow text wrapping
              width: rightSidebarWidth,
              minWidth: rightSidebarOpen ? 336 : 0, // 56 (mode switcher) + 280 (min content)
              height: '100%',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              transition: isResizing
                ? 'none'
                : theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                  }),
              borderLeft: rightSidebarOpen ? 'none' : '3px solid',
              borderLeftColor: colors.primary.main,
              backgroundColor: rightSidebarOpen
                ? colors.paper.background
                : colors.background.secondary,
            },
          }}
        >
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          {RightSidebarComponent && <RightSidebarComponent {...rightSidebarProps} />}
          {/* Resize Handle for Right Sidebar */}
          {rightSidebarOpen && !isMobile && (
            <ResizeHandle
              type="right"
              position="left"
              onMouseDown={handleMouseDown}
              isResizing={isResizing}
              resizeType={resizeType}
              primaryColor={colors.primary.main}
            />
          )}
        </Drawer>
      )}
    </Box>
  );
};

// PropTypes validation
/* eslint-disable react/require-default-props */
ProjectLayout.propTypes = {
  // Layout state
  leftSidebarOpen: PropTypes.bool.isRequired,
  rightSidebarOpen: PropTypes.bool.isRequired,
  toggleLeftSidebar: PropTypes.func.isRequired,
  toggleRightSidebar: PropTypes.func.isRequired,
  leftSidebarWidth: PropTypes.number.isRequired,
  rightSidebarWidth: PropTypes.number.isRequired,
  handleLeftSidebarResize: PropTypes.func.isRequired,
  handleRightSidebarResize: PropTypes.func.isRequired,

  // Project data
  activeProject: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string,
    type: PropTypes.string,
    createdAt: PropTypes.string.isRequired,
    updatedAt: PropTypes.string.isRequired,
    thinOwner: PropTypes.shape({
      name: PropTypes.string,
      id: PropTypes.string,
    }),
    thinChapters: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        id: PropTypes.string,
      })
    ),
    thinCoauthors: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        id: PropTypes.string,
      })
    ),
    thinEditors: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        id: PropTypes.string,
      })
    ),
    thinNotes: PropTypes.arrayOf(PropTypes.shape({})),
    isDeleted: PropTypes.bool,
  }),
  chapters: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      id: PropTypes.string,
    })
  ),
  notes: PropTypes.arrayOf(PropTypes.shape({})),
  characters: PropTypes.arrayOf(PropTypes.shape({})),
  collaborators: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      id: PropTypes.string,
    })
  ),

  // Handlers
  handleBackToDashboard: PropTypes.func.isRequired,
  handleEditProject: PropTypes.func.isRequired,
  handleAddChapter: PropTypes.func.isRequired,
  handleAddNote: PropTypes.func.isRequired,
  handleAddCharacter: PropTypes.func,
  onCharacterClick: PropTypes.func,
  handleChapterClick: PropTypes.func.isRequired,
  handleAddScene: PropTypes.func.isRequired,
  handleSceneClick: PropTypes.func,
  setRefreshScenesCallback: PropTypes.func.isRequired,
  onSceneAdded: PropTypes.func,
  formatDate: PropTypes.func.isRequired,
  suppressSceneLoading: PropTypes.bool,
  handleExportProject: PropTypes.func.isRequired,
  handleImportDocument: PropTypes.func,
  handleShareProject: PropTypes.func,
  activeCenterSection: PropTypes.string,
  notesView: PropTypes.string,
  corkboardId: PropTypes.string,
  timelineId: PropTypes.string,
  gameTool: PropTypes.string,
  onGameToolChange: PropTypes.func,
  onCenterSectionChange: PropTypes.func,
};
/* eslint-enable react/require-default-props */

export default ProjectLayout;
